// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later

import type { Tenths } from './types';

export const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const PRODUCT_NAMES: Record<string, string> = {
  ULP: 'Unleaded 91',
  E10: 'E10',
  P95: 'Premium 95',
  P98: 'Premium 98',
  DSL: 'Diesel',
  PDSL: 'Premium diesel',
  LPG: 'LPG',
  E85: 'E85',
  LRP: 'Lead replacement',
  B20: 'Biodiesel 20',
};

/** 1959 → "195.9" */
export function cents(v: Tenths | null | undefined, dp = 1): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return (v / 10).toFixed(dp);
}

/** 1959 → "195.9c" */
export function priceLabel(v: Tenths | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${cents(v)}c`;
}

/** A signed difference in tenths → "+4.2c" / "−4.2c" (true minus sign). */
export function deltaLabel(v: number | null | undefined, dp = 1): string {
  if (v == null || !Number.isFinite(v)) return '—';
  if (Math.abs(v) < 0.05) return '0.0c';
  const sign = v > 0 ? '+' : '−';
  return `${sign}${(Math.abs(v) / 10).toFixed(dp)}c`;
}

/** Cost of a tank, in dollars, for a price difference in tenths of a cent. */
export function tankSaving(diffTenths: number, litres = 55): string {
  const dollars = (Math.abs(diffTenths) / 10) * litres / 100;
  return `$${dollars.toFixed(2)}`;
}

export function num(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-AU');
}

/** "2026-08-10" → "10 Aug 2026" */
export function shortDate(iso: string): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(d)} ${months[Number(m) - 1]} ${y}`;
}

/** "2026-08-10" → "Monday 10 Aug 2026" */
export function longDate(iso: string): string {
  if (!iso) return '—';
  const dow = (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;
  return `${DAY_NAMES[dow]} ${shortDate(iso)}`;
}

/** Six-step ramp from cheap (green) to dear (red), shared by every view. */
export const PRICE_RAMP = ['#167a4a', '#6aa84f', '#c9b458', '#dd8a3c', '#c2410c', '#8c1d0b'];

/**
 * Colour for a price, positioned within an explicit [lo, hi] range.
 * A LINEAR scale, deliberately — a quantile scale on prices makes a 0.2c spread
 * look identical to a 40c spread, because it colours by rank rather than value.
 */
export function priceColour(v: number | null | undefined, lo: number, hi: number): string {
  if (v == null || !Number.isFinite(v) || hi <= lo) return '#d5cec2';
  const t = Math.min(1, Math.max(0, (v - lo) / (hi - lo)));
  const i = Math.min(PRICE_RAMP.length - 1, Math.floor(t * PRICE_RAMP.length));
  return PRICE_RAMP[i];
}

/**
 * Diverging scale for a quantity measured AROUND ZERO — used by the regime
 * ribbon, where each cell is a day's distance from its own week's average.
 *
 * A sequential price ramp is the wrong encoding for this and is wrong in a way
 * that is easy to miss: on a six-step cheap→dear ramp the midpoint lands on
 * orange, so a day that is exactly average reads as expensive, and a year with
 * no cycle at all paints as a wall of orange. A diverging scale puts neutral in
 * the middle where it belongs.
 */
export const DIVERGING_RAMP = ['#0b6e4f', '#4d9b70', '#a8cbb0', '#f0ece2', '#eeb489', '#d4763f', '#a83a17'];

export function divergingColour(v: number | null | undefined, limit: number): string {
  if (v == null || !Number.isFinite(v) || !(limit > 0)) return '#eceae5';
  const t = Math.max(-1, Math.min(1, v / limit));
  const i = Math.round(((t + 1) / 2) * (DIVERGING_RAMP.length - 1));
  return DIVERGING_RAMP[i];
}

/** Stable colour per category, for brands and products. */
const CATEGORY_COLOURS = [
  '#0e7c6b', '#b45309', '#3b6fb5', '#8b5cf6', '#c2410c', '#0f766e',
  '#a16207', '#be185d', '#4d7c0f', '#0369a1', '#7c2d12', '#6d28d9',
];
export function categoryColour(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return CATEGORY_COLOURS[h % CATEGORY_COLOURS.length];
}

/** Escape for insertion into innerHTML. */
export function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * Escape for a `data-tip` written into an HTML string. innerHTML needs the
 * numeric entity for a newline; setAttribute needs a RAW newline. Using the
 * wrong one renders a literal "&#10;" in the tooltip.
 */
export function escTip(s: string): string {
  return esc(s).replace(/\n/g, '&#10;');
}

export function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function regionSlug(state: string, region: string): string {
  return `${state}-${slugify(region)}`;
}
